import { SelectableTable } from "sj-ui-toolkit/src/components/SelectableTable/SelectableTable";
import { Checkbox } from "primereact/checkbox";
import { Fieldset } from "primereact/fieldset";
import { ValidationMessage } from "sj-ui-toolkit/src/components";
import type { TournamentLesson } from "@/api/tournaments";
import type { FilterGroup } from "@/helpers/tournamentFilters";
import type { LessonFilters } from "@/hooks/useTournamentLessons";

// Experiments are hard-coded to 5 minutes (legacy implementation value)
const EXPERIMENT_DURATION = 5;

// Experiments have no `duration` field, so fall back to EXPERIMENT_DURATION.
// Used for both the per-row display and the cumulative duration summary.
const getLessonDuration = (lesson: TournamentLesson) => lesson.duration ?? EXPERIMENT_DURATION;

interface TournamentContentProps {
  availableLessons: TournamentLesson[];
  selectedLessons: TournamentLesson[];
  filters: LessonFilters;
  filterOptions: FilterGroup[];
  onChangeFilters: (filters: LessonFilters) => void;
  onChangeLessons: (selected: TournamentLesson[]) => void;
  showValidationMessages: boolean;
  validationErrors: { hasError: boolean; message: string };
  isTournamentInProgress: boolean;
  isLoadingLessons?: boolean;
}

export function TournamentContent({
  availableLessons,
  selectedLessons,
  filters,
  filterOptions,
  onChangeFilters,
  onChangeLessons,
  showValidationMessages,
  validationErrors,
  isTournamentInProgress,
  isLoadingLessons = false,
}: TournamentContentProps) {
  const toggleFilterValue = (key: keyof LessonFilters, value: string) => {
    const current = filters[key];
    const nextValues = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    onChangeFilters({ ...filters, [key]: nextValues });
  };

  return (
    <>
      {!isTournamentInProgress && (
        <Fieldset
          legend="Filters"
          toggleable
          collapsed={false}
          className={`mb-4${isLoadingLessons ? " pointer-events-none opacity-50" : ""}`}
        >
          <div className="grid grid-cols-6 gap-4">
            {filterOptions.map(filterGroup => (
              <div key={filterGroup.label} className="max-h-96 overflow-y-auto atlas-scrollbar">
                <div className="font-semibold mb-2">{filterGroup.title}</div>
                <div className="space-y-2">
                  {filterGroup.filters.map(({ value, name }) => (
                    <div key={value} className="flex items-center gap-2">
                      <Checkbox
                        inputId={`${filterGroup.label}-${value}`}
                        checked={
                          (filters[filterGroup.label as keyof LessonFilters] ?? []).includes(
                            value
                          ) || false
                        }
                        onChange={() =>
                          toggleFilterValue(filterGroup.label as keyof LessonFilters, value)
                        }
                      />
                      <label htmlFor={`${filterGroup.label}-${value}`} className="cursor-pointer">
                        {name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Fieldset>
      )}

      <SelectableTable
        items={availableLessons}
        selected={selectedLessons}
        columns={[
          { field: "name", header: "Lesson Name" },
          {
            field: "duration",
            header: "Duration",
            body: (row: TournamentLesson) => `${getLessonDuration(row)} mins`,
          },
        ]}
        getKey={(lesson: TournamentLesson) => lesson.id ?? ""}
        getDuration={getLessonDuration}
        onChange={onChangeLessons}
        readOnly={isTournamentInProgress}
        isLoading={isLoadingLessons}
        showSelectionSummary
        emptyMessage={
          filters.activityType?.length === 1 && filters.activityType.includes("app_lab")
            ? "Not available for your organization. If you would like to enable these lessons, please reach out to your Account Manager."
            : undefined
        }
      />
      {showValidationMessages && validationErrors.hasError && (
        <ValidationMessage severity="error" text={validationErrors.message} />
      )}
    </>
  );
}
